import { el } from 'redom';

import { stringToSVG } from 'utilities';

import HelptapEditor from '../helpers';

import DuplicateNodeButton from './buttons/duplicate';
import CopyNodeButton from './buttons/copy';
import CopyNodeStylesButton from './buttons/copy-styles';
import DeletePageButton from './buttons/delete';
import FavouritePageButton from './buttons/favourite';
import TurnIntoButton from './buttons/turn-into';
import { buildLayoutButtons } from './buttons/layout-actions';
import AdvancedDuplicatePanel from './panels/advanced-duplicate-panel/content-panel';
import PanelToggle from './panels/panel-toggle';
import AdvancedDataCollectionChildrenSettingsSection from './panels/advanced-data-collection-children-settings';
import AdvancedColumnsSettingsPanel from './panels/advanced-columns-settings-panel';
import CanvasShapesPanel from './panels/canvas-shapes';
import CanvasExportsPanel from './panels/canvas-exports';
import VideoCanvasExportsPanel from './panels/video-canvas-exports';
import AdvancedDirectorySettingsSection from './panels/advanced-directory-settings';

import duplicateNodeIconString from 'assets/images/duplicate-node-icon.svg?raw';

const duplicateNodeIcon = stringToSVG(duplicateNodeIconString);

class MoreNodeActionsMenu {

  constructor(configuration) {

    this.menu = null;
    this.buttons = [];

    this.anchorReference = configuration.anchorReference;
    this.activeNodePath = configuration.activeNodePath;
    this.editor = configuration.editor;
    this.node = HelptapEditor.getNodeByPath(this.editor, configuration.activeNodePath);
    this.parentEntityId = configuration.parentEntityId;
    this.disableAdvancedDragHandleFeatures = configuration.disableAdvancedDragHandleFeatures;
    this.onClose = configuration.onClose;
    this.positionMenu = configuration.positionMenu;
    this.mediaUploader = configuration.mediaUploader;

    this.outsideEventRegistration = null;

    this.el = el('.more-node-actions-menu-wrapper', {
      onclick: this.handleWrapperClick.bind(this)
    }, [
      this.renderMenu()
    ]);

  }

  getDefaultOptionsForButtons() {

    return {
      editor: this.editor,
      node: this.node,
      parentEntityId: this.parentEntityId,
      activeNodePath: this.activeNodePath
    };

  }

  getDuplicateButton() {

    if (this.disableAdvancedDragHandleFeatures === false) {

      const contentPanel = new AdvancedDuplicatePanel({
        ...this.getDefaultOptionsForButtons(),
        onComplete: this.onClose.bind(this),
        currentPageId: this.parentEntityId,
        menu: this
      });
      return new PanelToggle({
        label: 'Duplicate',
        icon: duplicateNodeIcon.cloneNode(true),
        contentPanel,
        onExpand: this.repositionMenu.bind(this),
        onCollapse: this.repositionMenu.bind(this)
      });

    } else {
      return new DuplicateNodeButton(this, {
        editor: this.editor,
        node: this.node,
        activeNodePath: this.activeNodePath
      });
    }

  }

  repositionMenu() {

    this.positionMenu();

  }

  renderCopyButtons() {

    this.buttons.push(new CopyNodeButton(this, {
      ...this.getDefaultOptionsForButtons()
    }));

    this.buttons.push(new CopyNodeStylesButton(this, {
      ...this.getDefaultOptionsForButtons()
    }));

  }

  getFavouriteButton() {

    return new FavouritePageButton(this, {
      ...this.getDefaultOptionsForButtons()
    });

  }

  getDeleteButton() {

    return new DeletePageButton(this, {
      ...this.getDefaultOptionsForButtons()
    });

  }

  tryAddAdvancedDataCollectionChildrenSettingsButton() {

    if (HelptapEditor.dataCollection.isDataCollectionNode(this.node) === false) {
      return;
    }

    const button = new AdvancedDataCollectionChildrenSettingsSection({
      ...this.getDefaultOptionsForButtons(),
      menu: this
    });

    this.buttons.push(button);

  }

  tryAddAdvancedDirectorySettingsButton() {

    if (HelptapEditor.directory.isDirectoryNode(this.node) === false) {
      return;
    }

    const button = new AdvancedDirectorySettingsSection({
      ...this.getDefaultOptionsForButtons(),
      menu: this
    });

    this.buttons.push(button);

  }

  tryRenderAdvancedColumnsSettingsPanel() {

    if (HelptapEditor.multipleColumns.isColumnsNode(this.node) === false) {
      return;
    }

    const button = new AdvancedColumnsSettingsPanel({
      ...this.getDefaultOptionsForButtons(),
      menu: this
    });

    this.buttons.push(button);

  }

  tryRenderDuplicateButton() {

    if (HelptapEditor.dataCollection.isDataCollectionDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNodeDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDatabaseConnectedNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.directory.isDirectoryNode(this.node) === true) {
      return;
    }

    this.buttons.push(this.getDuplicateButton());

  }

  tryRenderFavouriteButton() {

    if (this.disableAdvancedDragHandleFeatures === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNodeDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDatabaseConnectedNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.directory.isDirectoryNode(this.node) === true) {
      return;
    }

    this.buttons.push(this.getFavouriteButton());

  }

  getTurnIntoButton() {

    const turnIntoButton = new TurnIntoButton({
      ...this.getDefaultOptionsForButtons(),
      menu: this,
      editor: this.editor,
      node: this.node,
    });

    return turnIntoButton;

  }

  tryRenderTurnIntoButton() {

    if (HelptapEditor.dataCollection.isDataCollectionDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNodeDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDatabaseConnectedNodeSelected(this.editor) === true) {
      return;
    }

    if (HelptapEditor.conversion.canConvertToAnotherType(this.editor, this.activeNodePath) === false) {
      return;
    }

    this.buttons.push(this.getTurnIntoButton());

  }

  tryRenderDeleteButton() {

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNode(this.node) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNodeDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNode(this.node) === true) {
      return;
    }

    this.buttons.push(this.getDeleteButton());

  }

  tryRenderCanvasButtons() {

    if (HelptapEditor.canvas.isCanvasNode(this.node) === false) {
      return;
    }

    const button = new CanvasShapesPanel({
      ...this.getDefaultOptionsForButtons(),
      mediaUploader: this.mediaUploader,
      menu: this
    });

    this.buttons.push(button);

  }

  tryRenderVideoCanvasButtons() {

    if (HelptapEditor.videoCanvas.isVideoCanvasNode(this.node) === false) {
      return;
    }

    const button = new CanvasShapesPanel({
      ...this.getDefaultOptionsForButtons(),
      mediaUploader: this.mediaUploader,
      menu: this,
      videoCanvas: true
    });

    this.buttons.push(button);

  }

  tryRenderCanvasExports() {

    if (HelptapEditor.canvas.isCanvasNode(this.node) === false) {
      return;
    }

    const button = new CanvasExportsPanel({
      ...this.getDefaultOptionsForButtons(),
      mediaUploader: this.mediaUploader,
      menu: this
    });

    this.buttons.push(button);

  }

  tryRenderVideoCanvasExports() {

    if (HelptapEditor.videoCanvas.isVideoCanvasNode(this.node) === false) {
      return;
    }

    const button = new VideoCanvasExportsPanel({
      ...this.getDefaultOptionsForButtons(),
      mediaUploader: this.mediaUploader,
      menu: this
    });

    this.buttons.push(button);

  }

  tryRenderLayoutButtons() {

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNodeDescendant(this.editor, this.activeNodePath) === true) {
      return;
    }

    this.buttons.push(...buildLayoutButtons(this, this.getDefaultOptionsForButtons()));

  }

  renderMenu() {

    const buttons = this.buttons;

    this.tryRenderCanvasButtons();
    this.tryRenderCanvasExports();
    this.tryRenderVideoCanvasButtons();
    this.tryRenderVideoCanvasExports();
    this.renderCopyButtons();
    this.tryRenderDuplicateButton();
    this.tryRenderFavouriteButton();
    this.tryRenderTurnIntoButton();
    this.tryRenderLayoutButtons();
    this.tryAddAdvancedDataCollectionChildrenSettingsButton();
    this.tryAddAdvancedDirectorySettingsButton();
    this.tryRenderAdvancedColumnsSettingsPanel();
    this.tryRenderDeleteButton();

    this.menu = el('div.node-management-controls', buttons);

    return this.menu;

  }

  close() {

    this.onClose();

  }

  outsideClickHandler() {

    this.onClose();

  }

  handleWrapperClick(event) {

  }

  updateButtons() {

    const buttons = this.buttons;

    for (let button of buttons) {

      button.update({
        editor: this.editor,
        node: this.node,
        parentEntityId: this.parentEntityId,
        activeNodePath: this.activeNodePath
      });

    }

  }

  update(configuration) {

    this.anchorReference = configuration.anchorReference;
    this.activeNodePath = configuration.activeNodePath;
    this.editor = configuration.editor;
    this.node = HelptapEditor.getNodeByPath(this.editor, configuration.activeNodePath);
    this.parentEntityId = configuration.parentEntityId;
    this.onClose = configuration.onClose;

    this.updateButtons();

    // displayElementAroundAnchor(this.el, this.anchorReference.current, void 0, 5, 5, 'left', false);

  }

  onmount() {

  }

  onunmount() {

  }

}

export default MoreNodeActionsMenu;